/**
 * Faux backend, pour développer sans serveur.
 *
 * Il remplace `window.fetch` et répond aux routes du parcours de mise en route. Tout le
 * reste du code s'exécute normalement — `apiFetchRoute`, `fetchWithToken`, les écrans,
 * la validation : seule la réponse HTTP est fabriquée.
 *
 * Lancement : `npm run start:mock`. `REACT_APP_MOCK_API` est déclarée à `false` dans
 * `.env` pour que webpack élimine l'import — laissée absente, elle deviendrait un test
 * évalué à l'exécution et le module partirait quand même dans le bundle.
 *
 * Les données vivent dans localStorage, donc un rafraîchissement ne perd rien — c'est
 * justement ce que le parcours doit savoir encaisser. `window.mockReset()` repart de zéro.
 */
import {
  Breeding,
  Building,
  DeviceWithoutLegacyPassword,
} from "../generated/types";

const STORE_KEY = "mock-api-store";

type MockStore = {
  breedings: Breeding[];
  buildings: Building[];
  devices: DeviceWithoutLegacyPassword[];
  nextId: number;
};

const EMPTY_STORE: MockStore = {
  breedings: [],
  buildings: [],
  devices: [],
  nextId: 1,
};

const readStore = (): MockStore => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as MockStore) : EMPTY_STORE;
  } catch {
    return EMPTY_STORE;
  }
};

const writeStore = (store: MockStore) => {
  localStorage.setItem(STORE_KEY, JSON.stringify(store));
};

/** Le compte de test : un éleveur, entreprise 1. */
const ACCOUNT = {
  id: 1,
  type: "breeder_assistant",
  language: "fr",
  id_company: 1,
  email: "eleveur@test.local",
  first_name: "Camille",
  last_name: "Durand",
  phone_mobile: "0600000000",
  phone_fixe: "",
  creation_date: "2026-01-01",
  avatar: "",
  is_activated: true,
};

const COMPANY = {
  id: 1,
  id_x3: "X3-TEST",
  creation_date: "2026-01-01",
  company_name: "EARL des Quatre Vents",
  siret: "12345678901234",
  address: "12 route des Granges",
  address_complementary: "",
  zip_code: "35000",
  city: "Rennes",
  country: "FR",
  phone_mobile: "0600000000",
  phone_fixe: "",
  email: "contact@test.local",
};

const json = (body: unknown, status = 200) =>
  new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });

/** Bâtiment complet à partir des champs envoyés par le formulaire. */
const buildBuilding = (payload: Record<string, unknown>, id: number) =>
  ({ ...payload, id }) as unknown as Building;

const handle = (
  path: string,
  method: string,
  body: Record<string, unknown>
): Response | null => {
  const store = readStore();

  if (path === "/login") return json({ accessToken: "mock-token" });
  if (path === "/get-cookies") return json({});
  if (path === "/logout") return json({});
  if (path === "/account/profile")
    return json({ account: ACCOUNT, company: COMPANY });

  if (path === "/breedings" && method === "GET") return json(store.breedings);
  if (path === "/breedings" && method === "POST") {
    const id = store.nextId;
    const breeding = { ...body, id } as unknown as Breeding;
    writeStore({
      ...store,
      breedings: [...store.breedings, breeding],
      nextId: id + 1,
    });
    return json({ id }, 201);
  }

  if (path === "/buildings" && method === "GET") return json(store.buildings);
  if (path === "/buildings" && method === "POST") {
    const id = store.nextId;
    writeStore({
      ...store,
      buildings: [...store.buildings, buildBuilding(body, id)],
      nextId: id + 1,
    });
    return json({ id }, 201);
  }

  if (path === "/devices" && method === "GET") return json(store.devices);
  if (path === "/devices/associate-device-to-user") {
    const uuid = String(body.uuid ?? "");
    // Le vrai backend répond 404 quand le produit n'existe pas ou est déjà pris : on
    // reproduit le cas sur un numéro de série réservé, pour pouvoir tester l'erreur.
    if (uuid === "000000000") return json({}, 404);

    const id = store.nextId;
    const idBuilding = Number(body.id_building) || 0;
    const device = {
      id,
      uuid,
      type: body.type,
      name: "",
    } as unknown as DeviceWithoutLegacyPassword;

    writeStore({
      ...store,
      devices: [...store.devices, device],
      // Le produit est rattaché au bâtiment : c'est ce que lit le récapitulatif.
      buildings: store.buildings.map((building) =>
        building.id === idBuilding
          ? { ...building, device: [...building.device, id] }
          : building
      ),
      nextId: id + 1,
    });
    return json({ id }, 200);
  }

  // Le dashboard n'a pas de configuration enregistrée : il partira de ses widgets par défaut.
  if (path === "/dashboard/config") return json({}, 204);

  return null;
};

export const installMockServer = () => {
  const base = process.env.REACT_APP_URL_SERVER_DATA ?? "";
  const realFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input.toString();

    // Tout ce qui ne vise pas le backend (météo, actualités…) part normalement.
    if (!base || !url.startsWith(base)) return realFetch(input, init);

    const path = url.slice(base.length).split("?")[0];
    const method = (init?.method ?? "GET").toUpperCase();
    const body =
      typeof init?.body === "string"
        ? (JSON.parse(init.body) as Record<string, unknown>)
        : {};

    const response = handle(path, method, body);
    if (response) return response;

    console.warn(`[mock] route non simulée : ${method} ${path}`);
    return json({ error: "route non simulée" }, 404);
  };

  // Session ouverte d'office : il n'y a pas de vrai compte pour se connecter.
  localStorage.setItem("accessToken", "mock-token");
  localStorage.setItem(
    "accountStorage",
    JSON.stringify({
      first_name: ACCOUNT.first_name,
      last_name: ACCOUNT.last_name,
      company_name: COMPANY.company_name,
      email: ACCOUNT.email,
      type: ACCOUNT.type,
      avatar: ACCOUNT.avatar,
    })
  );

  const reset = () => {
    localStorage.removeItem(STORE_KEY);
    // Drapeaux liés au compte de test : sans ça le parcours ne se reproposerait pas.
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.startsWith("setup-resolved") ||
          key.startsWith("tour-seen") ||
          key === "dashboard-tutorial-seen" ||
          key === "dashboard-config"
      )
      .forEach((key) => localStorage.removeItem(key));
    window.location.href = "/";
  };

  (window as unknown as { mockReset: () => void }).mockReset = reset;

  console.info(
    "[mock] faux backend actif — compte éleveur de test.\n" +
      "       mockReset() dans la console pour repartir d'un compte vierge."
  );
};
